import { useState } from 'react'

import { ConfirmationDialog } from '@/components/ui/confirmation-dialog'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { type SequenceTemplate, useSequenceTemplates } from '@/models/sequenceTemplate'
import { CreateSequenceTemplateButton } from './CreateSequenceTemplateButton'

interface SequenceTemplateSelectProps {
  /** Receives the picked template; its steps only prefill, nothing stays linked to it. */
  onSelect: (template: SequenceTemplate) => void
  /** Ask first, because the pick overwrites steps the rep already has. */
  confirmReplace?: boolean
  className?: string
}

export function SequenceTemplateSelect({
  onSelect,
  confirmReplace = false,
  className,
}: SequenceTemplateSelectProps) {
  const { data: templates = [], isSuccess } = useSequenceTemplates()
  const [pending, setPending] = useState<SequenceTemplate | null>(null)

  function handleChange(id: string) {
    const template = templates.find((t) => t.id === id)
    if (!template) return
    if (confirmReplace) {
      setPending(template)
    } else {
      onSelect(template)
    }
  }

  if (!isSuccess) return null
  if (templates.length === 0) return <CreateSequenceTemplateButton />

  const groups = [
    { label: 'Global', items: templates.filter((t) => t.isGlobal) },
    { label: 'Personal', items: templates.filter((t) => !t.isGlobal) },
  ].filter((group) => group.items.length > 0)

  return (
    <>
      {/* Always shows the placeholder: a pick only prefills, so there's no selection to keep. */}
      <Select value="" onValueChange={handleChange}>
        <SelectTrigger className={cn('w-full', className)} aria-label="Sequence template">
          <SelectValue placeholder="Start from a template" />
        </SelectTrigger>
        <SelectContent>
          {groups.map((group) => (
            <SelectGroup key={group.label}>
              <SelectLabel>{group.label}</SelectLabel>
              {group.items.map((t) => (
                <SelectItem key={t.id} value={t.id}>
                  {t.name || 'Untitled'}
                </SelectItem>
              ))}
            </SelectGroup>
          ))}
        </SelectContent>
      </Select>
      <ConfirmationDialog
        open={pending !== null}
        onOpenChange={(open) => {
          if (!open) setPending(null)
        }}
        title="Replace the current steps?"
        description={
          pending
            ? `The current steps are replaced with the ones in ${pending.name || 'Untitled'}.`
            : undefined
        }
        confirmLabel="Replace steps"
        onConfirm={() => {
          if (pending) onSelect(pending)
        }}
      />
    </>
  )
}
